import { Injectable, NgZone, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  Cart,
  EventItemModel,
  EventService,
  OrderCalculation,
  OrderCompleteResponse,
  OrderCustomer,
  OrderService,
  SeatCoordinatePriceGrade,
  SeatLock,
} from 'api';
import { catchError, map, of, tap, throwError } from 'rxjs';
import { NotificationModalServiceService } from '../shared/services/notification-modal-service.service';
import { Router } from '@angular/router';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { AnalyticsService } from '../shared/services/analytics.service';
import { SegmentService, ProductAddedProperties } from 'segment';

interface TicketingCartStateModel {
  draftItem: Partial<Cart['items'][number]> | null;
  eventItemModel: EventItemModel | null;
  seatingFilters: Record<string, string[]>;
  calculation: OrderCalculation | null;
  seatLock: (SeatLock & { seatCoordinates: SeatCoordinatePriceGrade[] }) | null;
  promoCode: string | null;
  orderComplete?: OrderCompleteResponse | null;
}

export namespace TicketingQuantitiesActions {
  export class SetTicketQuantities {
    static readonly type = '[Ticketing Quantities Page] Set Ticket Quantities';

    constructor(public payload: { adults: number; children: number }) {}
  }
}

export namespace TicketingGuardActions {
  export class SetEventCodeAndDateTime {
    static readonly type = '[Ticketing Guard] Set event code and date time';

    constructor(public payload: { eventCode: string; eventDateTime: string }) {}
  }
}

export namespace TicketingTimerActions {
  export class ClearCartState {
    static readonly type = '[Ticketing Timer] Clear Cart State';
  }
}

export namespace TicketingItemActions {
  export class ClearCartState {
    static readonly type = '[Ticketing Item] Clear Cart State';
  }
}

export namespace TicketingSeatsActions {
  export class AddSelectedSeat {
    static readonly type = '[Ticketing Seats Page] Add Selected Seat';

    constructor(public seat: SeatCoordinatePriceGrade) {}
  }

  export class RemoveSelectedSeat {
    static readonly type = '[Ticketing Seats Page] Remove Selected Seat';

    constructor(public seat: SeatCoordinatePriceGrade) {}
  }

  export class ClearSelectedSeats {
    static readonly type = '[Ticketing Selected Page] Clear Selected Seat';

    constructor() {}
  }

  export class UnlockSeats {
    static readonly type = '[Ticketing Seats] Unlock Seats';
    constructor() {}
  }

  export class SetSeatingFilters {
    static readonly type = '[Ticketing Filters Component] Set Seating Filters';

    constructor(public payload: Record<string, string[]>) {}
  }

  export class SelectBestAvailable {
    static readonly type = '[Ticketing Seat Options] Select Best Available';
  }

  export class ChangeDateTimeOrQuantity {
    static readonly type =
      '[Ticketing Selected Event Bar] Change Date Time or Quantity';
    constructor(
      public payload: {
        eventDateTime: string | null;
        quantities: {
          adults: number | null;
          children: number | null;
        };
      }
    ) {}
  }

  export class Calculate {
    static readonly type = '[Ticketing Seats] Calculate';
    constructor() {}
  }

  export class ClearCalculation {
    static readonly type = '[Ticketing Seats] Clear Calculation';
  }

  export class LockSeats {
    static readonly type = '[Ticketing Seats] Lock Seats';
    constructor() {}
  }
}

export namespace TicketingCheckoutActions {
  export class ApplyPromoCode {
    static readonly type = '[Ticketing Checkout] Apply Promo Code';
    constructor(
      public promoCode: string,
      public customer: OrderCustomer
    ) {}
  }

  export class OrderCompleted {
    static readonly type = '[Ticketing Checkout] Order Completed';
    constructor(public orderComplete: OrderCompleteResponse) {}
  }

  export class RemovePromoCode {
    static readonly type = '[Ticketing Checkout] Remove Promo Code';
  }

  export class Calculate {
    static readonly type = '[Ticketing Checkout Promo] Calculate';
    constructor(
      public cart?: Cart,
      public captcha?: string
    ) {}
  }
}

class FetchEvent {
  static readonly type = '[Ticketing Cart State] Fetch Event';
}

const defaultTicketingCartStateModel: TicketingCartStateModel = {
  draftItem: null,
  eventItemModel: null,
  seatingFilters: {},
  calculation: null,
  seatLock: null,
  promoCode: null,
};

@State<TicketingCartStateModel>({
  name: 'ticketingCart',
  defaults: defaultTicketingCartStateModel,
})
@Injectable()
export class TicketingCartState {
  private eventService = inject(EventService);
  private notificationModalServiceService = inject(
    NotificationModalServiceService
  );
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private orderService = inject(OrderService);
  private analyticsService = inject(AnalyticsService);
  private segmentService = inject(SegmentService);

  @Action(FetchEvent)
  fetchEvent(
    { getState, patchState }: StateContext<TicketingCartStateModel>,
    {}: FetchEvent
  ) {
    const draftItem = getState().draftItem;
    if (draftItem?.eventCode && draftItem?.eventDateTime) {
      return this.eventService
        .getEventCalendar(
          'OT',
          undefined,
          draftItem.eventCode,
          draftItem.eventDateTime,
          draftItem.eventDateTime
        )
        .pipe(
          tap((event) =>
            patchState({
              eventItemModel:
                event.eventMap[
                  typeof draftItem.eventDateTime === 'string'
                    ? draftItem.eventDateTime?.split('T')[0]!
                    : draftItem.eventDateTime?.toISOString().split('T')[0]!
                ][0],
            })
          ),
          map(() => true),
          catchError(() => {
            this.notificationModalServiceService.showMessage({
              title: 'Event Not Found',
              message: 'The event could not be found',
            });
            this.router.navigate(['/ticketing']);
            return of(false);
          })
        );
    }
    return of(false);
  }

  @Action(TicketingGuardActions.SetEventCodeAndDateTime)
  setDraftItem(
    { getState, patchState, dispatch }: StateContext<TicketingCartStateModel>,
    { payload }: TicketingGuardActions.SetEventCodeAndDateTime
  ) {
    if (
      getState().draftItem?.eventCode === payload.eventCode &&
      getState().draftItem?.eventDateTime === payload.eventDateTime
    ) {
      return;
    }

    // If the date and time has changed, unlock the seats
    if (getState().seatLock) {
      dispatch(new TicketingSeatsActions.UnlockSeats());
    }

    patchState({
      draftItem: {
        type: 'SHOW',
        eventCode: payload.eventCode,
        eventDateTime: payload.eventDateTime,
      },
    });
    return dispatch(new FetchEvent());
  }

  @Action(TicketingQuantitiesActions.SetTicketQuantities)
  setTicketQuantities(
    { getState, patchState }: StateContext<TicketingCartStateModel>,
    { payload }: TicketingQuantitiesActions.SetTicketQuantities
  ) {
    const details = [
      { designation: 'ADULT', count: payload.adults },
      { designation: 'CHILD', count: payload.children },
    ];

    patchState({
      draftItem: {
        ...getState().draftItem,
        details,
        // If the total quantity is less than the current selected seats, clear the selected seats
        ...(TicketingCartSelectors.totalQuantity(getState()) >
          payload.adults + payload.children && {
          seatCoordinates: [],
        }),
      },
    });
  }

  @Action(TicketingSeatsActions.AddSelectedSeat)
  addSelectedSeat(
    { getState, patchState, dispatch }: StateContext<TicketingCartStateModel>,
    { seat }: TicketingSeatsActions.AddSelectedSeat
  ) {
    patchState({
      draftItem: {
        ...getState().draftItem,
        seatCoordinates: [
          ...(getState().draftItem?.seatCoordinates || []),
          seat,
        ],
      },
    });
  }

  @Action(TicketingSeatsActions.RemoveSelectedSeat)
  removeSelectedSeat(
    { getState, patchState }: StateContext<TicketingCartStateModel>,
    { seat }: TicketingSeatsActions.RemoveSelectedSeat
  ) {
    patchState({
      draftItem: {
        ...getState().draftItem,
        seatCoordinates: getState().draftItem?.seatCoordinates?.filter(
          (selectedSeat) =>
            selectedSeat.rowNumber !== seat.rowNumber ||
            selectedSeat.seatNumber !== seat.seatNumber ||
            selectedSeat.sectionCode !== seat.sectionCode
        ),
      },
    });
  }

  @Action(TicketingSeatsActions.SetSeatingFilters)
  setSeatingFilters(
    { patchState }: StateContext<TicketingCartStateModel>,
    { payload }: TicketingSeatsActions.SetSeatingFilters
  ) {
    patchState({
      seatingFilters: payload,
    });
  }

  @Action(TicketingSeatsActions.SelectBestAvailable)
  selectBestAvailable(
    { getState, patchState }: StateContext<TicketingCartStateModel>,
    {}: TicketingSeatsActions.SelectBestAvailable
  ) {
    const eventCode = getState().draftItem?.eventCode;
    const eventDateTime = getState().draftItem?.eventDateTime;
    const quantity = TicketingCartSelectors.totalQuantity(getState());

    if (!eventCode || !eventDateTime) {
      return;
    }

    return this.eventService
      .getBestAvailableSeats(
        eventCode,
        eventDateTime,
        { quantity },
        getState().seatingFilters
      )
      .pipe(
        tap(({ seats }) => {
          patchState({
            draftItem: {
              ...getState().draftItem,
              seatCoordinates: seats,
            },
          });
        })
      );
  }

  @Action(TicketingSeatsActions.ChangeDateTimeOrQuantity)
  changeDateTimeOrQuantity(
    { getState, patchState, dispatch }: StateContext<TicketingCartStateModel>,
    { payload }: TicketingSeatsActions.ChangeDateTimeOrQuantity
  ) {
    const hasEventChanged =
      payload.eventDateTime !==
      (getState().eventItemModel?.eventDateTime as any as string);

    const adults =
      coerceNumberProperty(payload.quantities.adults) ??
      getState().draftItem?.details?.find(
        (detail) => detail.designation === 'ADULT'
      )?.count ??
      0;

    const children =
      coerceNumberProperty(payload.quantities.children) ??
      getState().draftItem?.details?.find(
        (detail) => detail.designation === 'CHILD'
      )?.count ??
      0;

    const selectedSeatsGreaterThanTotalQuantity =
      (getState().draftItem?.seatCoordinates?.length ?? 0) > adults + children;

    patchState({
      draftItem: {
        ...getState().draftItem,
        ...((hasEventChanged || selectedSeatsGreaterThanTotalQuantity) && {
          seatCoordinates: [],
        }),
        eventDateTime:
          payload.eventDateTime ?? getState().draftItem?.eventDateTime,
        details: [
          {
            designation: 'ADULT',
            count: adults,
          },
          {
            designation: 'CHILD',
            count: children,
          },
        ],
      },
    });
    if (selectedSeatsGreaterThanTotalQuantity) {
      this.notificationModalServiceService.showMessage({
        title: 'Selected Seats Exceed Quantity',
        message:
          'The number of selected seats exceeds the total quantity of tickets.  Your selected seats have been cleared.',
      });
      return;
    }
    if (hasEventChanged) {
      dispatch(new FetchEvent());
      return this.ngZone.run(() => {
        this.router.navigate([
          '/ticketing',
          getState().draftItem?.eventCode,
          payload.eventDateTime,
          'seats',
        ]);
      });
    }
    return;
  }

  @Action([TicketingSeatsActions.Calculate, TicketingCheckoutActions.Calculate])
  calculate(
    { getState, patchState }: StateContext<TicketingCartStateModel>,
    { captcha, cart }: TicketingCheckoutActions.Calculate
  ) {
    const item = getState().draftItem;
    if (
      !item?.type ||
      !item?.eventCode ||
      !item?.eventDateTime ||
      !item?.details?.length ||
      !item?.seatCoordinates?.length
    ) {
      console.error('Invalid cart item', item);
      return of(null);
    }
    const rollBackState = getState().calculation;
    patchState({ calculation: defaultTicketingCartStateModel.calculation });
    return this.orderService
      .calculateOrderPrice(
        cart ?? { items: [getState().draftItem as Cart['items'][number]] }
      )
      .pipe(
        tap((result) => patchState({ calculation: result })),
        // If the calculation fails, we roll back to the previous state)
        catchError((err) => {
          console.error('Error calculating order price', err);
          //roll back state to prior calculation
          patchState({ calculation: rollBackState });
          return throwError(() => err);
        })
      );
  }

  @Action(TicketingSeatsActions.ClearCalculation)
  clearCalculation({
    patchState,
    getState,
  }: StateContext<TicketingCartStateModel>) {
    if (getState().calculation) {
      patchState({ calculation: defaultTicketingCartStateModel.calculation });
    }
  }

  @Action(TicketingSeatsActions.LockSeats)
  lockSeats({ getState, patchState }: StateContext<TicketingCartStateModel>) {
    const { eventCode, eventDateTime } =
      TicketingCartSelectors.getEventCodeAndDateTime(getState())!;
    if (!eventCode || !eventDateTime) {
      return throwError(() => 'Event code or date time not set');
    }

    const ticketQuantity = TicketingCartSelectors.totalQuantity(getState())!;

    if (!ticketQuantity) {
      return throwError(() => 'No ticket quantity');
    }

    const seatCoordinates =
      TicketingCartSelectors.getSeatCoordinates(getState())!;

    if (!seatCoordinates.length) {
      return throwError(() => 'No seats selected');
    }
    const itemNumber =
      TicketingCartSelectors.getCalculationItemNumber(getState())!;

    if (!itemNumber) {
      return throwError(() => 'No item number');
    }

    const seatLockToken = TicketingCartSelectors.getSeatLockToken(getState());

    const lockBody = {
      seatCoordinates,
      ticketQuantity,
      itemNumber,
      seatLockToken,
    };

    return this.eventService.lockSeats(eventCode, eventDateTime, lockBody).pipe(
      tap((seatLock) => {
        patchState({ seatLock: { ...seatLock, seatCoordinates } });
        this.analyticsService.trackAddToCart(
          TicketingCartSelectors.getCalculation(getState())!
        );
        const productAddedProperties: ProductAddedProperties = {
          quantity: ticketQuantity,
          sku: eventCode,
          name: getState().eventItemModel?.eventName,
          category: 'Tickets',
          theaterLocation: getState().eventItemModel?.theaterLocation,
          eventDateTime,
        };
        this.segmentService.trackEvent('Product Added', productAddedProperties);
      })
    );
  }

  @Action([
    TicketingTimerActions.ClearCartState,
    TicketingItemActions.ClearCartState,
  ])
  clearCartState({ patchState }: StateContext<TicketingCartStateModel>) {
    patchState(defaultTicketingCartStateModel);
  }

  @Action(TicketingSeatsActions.UnlockSeats)
  unlockSeats({ getState, patchState }: StateContext<TicketingCartStateModel>) {
    const { eventCode, eventDateTime } =
      TicketingCartSelectors.getEventCodeAndDateTime(getState())!;
    if (!eventCode || !eventDateTime) {
      return throwError(() => 'Event code or date time not set');
    }

    const seatLockToken = TicketingCartSelectors.getSeatLockToken(getState());

    if (!seatLockToken) {
      return throwError(() => 'No seat lock token');
    }

    const body = { seatLockToken };

    return this.eventService.unlockSeats(eventCode, eventDateTime, body).pipe(
      tap(() => {
        patchState({ seatLock: null });
      })
    );
  }

  @Action(TicketingCheckoutActions.ApplyPromoCode)
  checkoutPageApplyPromoCode(
    { dispatch, patchState, getState }: StateContext<TicketingCartStateModel>,
    { promoCode, customer }: TicketingCheckoutActions.ApplyPromoCode
  ) {
    return dispatch(
      new TicketingCheckoutActions.Calculate({
        promoCode,
        items: [getState().draftItem as Cart['items'][number]],
        customer,
      })
    ).pipe(
      tap(() =>
        patchState({
          promoCode,
        })
      )
    );
  }

  @Action(TicketingCheckoutActions.RemovePromoCode)
  removePromoCode({
    patchState,
    dispatch,
    getState,
  }: StateContext<TicketingCartStateModel>) {
    return dispatch(
      new TicketingCheckoutActions.Calculate({
        items: [getState().draftItem as Cart['items'][number]],
      })
    ).pipe(
      tap(() =>
        patchState({
          promoCode: null,
        })
      )
    );
  }

  @Action(TicketingCheckoutActions.OrderCompleted)
  orderCompleted(
    { setState, getState }: StateContext<TicketingCartStateModel>,
    { orderComplete }: TicketingCheckoutActions.OrderCompleted
  ) {
    const currentSeatCoordinates = getState().draftItem?.seatCoordinates;
    setState({
      ...defaultTicketingCartStateModel,
      orderComplete,
      draftItem: {
        ...getState().draftItem,
        seatCoordinates: currentSeatCoordinates,
      },
    });
  }

  @Action(TicketingSeatsActions.ClearSelectedSeats)
  clearSelectedSeat({
    patchState,
    getState,
  }: StateContext<TicketingCartStateModel>) {
    patchState({
      draftItem: {
        ...getState().draftItem,
        seatCoordinates: [],
      },
    });
  }
}

export class TicketingCartSelectors {
  @Selector([TicketingCartState])
  static getDraftItem(state: TicketingCartStateModel) {
    return state.draftItem;
  }

  @Selector([TicketingCartState])
  static getEventItemModel(state: TicketingCartStateModel) {
    return state.eventItemModel;
  }

  @Selector([TicketingCartState])
  static getSeatCoordinates(state: TicketingCartStateModel) {
    return state.draftItem?.seatCoordinates || [];
  }

  @Selector([TicketingCartState])
  static getSeatLockToken(state: TicketingCartStateModel) {
    return state.seatLock?.seatLockToken;
  }

  @Selector([TicketingCartState])
  static getLockedSeatCoordinates(state: TicketingCartStateModel) {
    return state.seatLock?.seatCoordinates;
  }

  @Selector([TicketingCartState])
  static getTicketQuantities(state: TicketingCartStateModel) {
    return (
      state.draftItem?.details?.reduce(
        (acc, curr) => {
          if (curr.designation === 'ADULT') {
            acc.adults = curr.count;
          } else if (curr.designation === 'CHILD') {
            acc.children = curr.count;
          }
          return acc;
        },
        { adults: 0, children: 0 }
      ) ?? null
    );
  }

  @Selector([TicketingCartState])
  static totalQuantity(state: TicketingCartStateModel) {
    return (
      state.draftItem?.details?.reduce((acc, curr) => acc + curr.count, 0) || 0
    );
  }

  @Selector([TicketingCartState])
  static totalSelectedSeats(state: TicketingCartStateModel) {
    return state.draftItem?.seatCoordinates?.length || 0;
  }

  @Selector([TicketingCartState])
  static getEventCodeAndDateTime(state: TicketingCartStateModel) {
    return {
      eventCode: state.draftItem?.eventCode,
      eventDateTime: state.draftItem?.eventDateTime,
    };
  }

  @Selector([TicketingCartState])
  static getSeatingFilters(state: TicketingCartStateModel) {
    return state.seatingFilters;
  }

  @Selector([TicketingCartState])
  static getCalculation(state: TicketingCartStateModel) {
    return state.calculation;
  }

  @Selector([TicketingCartState])
  static getCalculationItemNumber(state: TicketingCartStateModel) {
    return state.calculation?.items.find((item) => item.type === 'SHOW')
      ?.itemNumber;
  }

  @Selector([TicketingCartState])
  static getTheaterCode(state: TicketingCartStateModel) {
    return state.eventItemModel?.theaterLocation;
  }

  @Selector([TicketingCartState])
  static getSeatLockExpiryDateTime(state: TicketingCartStateModel) {
    return state.seatLock?.lockExpiryDateTime;
  }

  @Selector([TicketingCartState])
  static getPromoCode(state: TicketingCartStateModel) {
    return state.promoCode;
  }

  @Selector([TicketingCartState])
  static getTotalSaving(state: TicketingCartStateModel) {
    return state.calculation?.totalSavings || 0;
  }

  @Selector([TicketingCartState])
  static getOrderCompleted(state: TicketingCartStateModel) {
    return state.orderComplete ?? null;
  }
}
